import { HttpStatusCode } from '@thesavingsgroup/enums/HttpStatusCode';
import lodashGet from 'lodash/get';
import { StateCreator } from 'zustand';

import { ApiState } from './types/ApiState';
import { ApiStateDependencies } from './types/ApiStateDependencies';
import { HttpClientInstance } from './types/HttpClientInstance';

export const apiStore =
  ({
    config,
    httpClient,
    useParameterStore,
    useSignOutStore,
  }: ApiStateDependencies): StateCreator<ApiState> =>
  (set, get) => ({
    isLoading: false,

    featureApi: (): HttpClientInstance => {
      const featureHttpRequest = httpClient.create({
        headers: {
          Authorization: config.unleash.clientKey,
          'Content-Type': 'application/json',
        },
        timeout: 15_000,
      });

      featureHttpRequest.interceptors.response.use((response): any => {
        return response.data;
      });

      return featureHttpRequest;
    },
    handleUnauthorized: (error: unknown): void => {
      const statusCode = lodashGet(error as any, 'response.status');

      const { token } = useParameterStore.getState();
      const { signOut } = useSignOutStore.getState();

      if (statusCode === HttpStatusCode.UNAUTHORIZED && token) {
        signOut({
          withAuthRedirectPath: window.location.pathname,
        });
      }
    },
    httpRequest: (): HttpClientInstance => httpClient.create(),
    portalApi: (): HttpClientInstance => {
      const portalHttpRequest = httpClient.create({
        baseURL: new URL(config.api.path, config.api.baseUrl).href,
        headers: {
          'x-tsg-current-hash': import.meta.env.VITE_APP_COMMIT_HASH,
          'x-tsg-current-url': window.location.href,
          'x-tsg-current-version': import.meta.env.VITE_APP_VERSION,
        },
        withCredentials: true,
      });

      portalHttpRequest.interceptors.request.use((requestConfig): any => {
        set({ isLoading: true });

        const { token } = useParameterStore.getState();

        if (token && requestConfig?.headers) {
          requestConfig.headers['X-Access-Token'] = token;
        }
        return requestConfig;
      });

      portalHttpRequest.interceptors.response.use(
        (response): any => {
          set({ isLoading: false });

          const {
            data: { token, ...data },
          } = response;

          if (token) {
            useParameterStore.setState({ token });
          }

          return response.data || data;
        },
        (error): Promise<never> => {
          set({ isLoading: false });

          get().handleUnauthorized(error);

          return Promise.reject(error);
        },
      );

      return portalHttpRequest;
    },
  });
